<template>
  <div>
    <div class="flex justify-between items-center mb-6">
      <div>
        <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Testimonials Verwaltung
        </h2>
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Verwalte Kundenbewertungen und -stimmen für die Startseite
        </p>
      </div>
      <button
        @click="openCreateModal"
        class="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        <span>Neues Testimonial</span>
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
    </div>

    <!-- Empty State -->
    <div v-else-if="testimonials.length === 0" class="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700">
      <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
      </svg>
      <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">Keine Testimonials</h3>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Erstelle dein erstes Testimonial.</p>
    </div>

    <!-- Testimonials Grid -->
    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <div
        v-for="testimonial in testimonials"
        :key="testimonial.id"
        class="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6 relative group"
      >
        <!-- Visibility & Project Badge -->
        <div class="absolute top-4 right-4 flex flex-col items-end space-y-2">
          <span
            :class="[
              'px-2 py-1 text-xs font-semibold rounded',
              testimonial.isVisible
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
            ]"
          >
            {{ testimonial.isVisible ? 'Sichtbar' : 'Versteckt' }}
          </span>
          <span
            v-if="getProjectName(testimonial.projectId)"
            class="px-2 py-1 text-xs font-semibold rounded bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 flex items-center space-x-1"
            :title="`Verknüpft mit: ${getProjectName(testimonial.projectId)}`"
          >
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            <span class="truncate max-w-[100px]">{{ getProjectName(testimonial.projectId) }}</span>
          </span>
        </div>

        <!-- Profilbild -->
        <div class="flex justify-center mb-4">
          <img
            :src="testimonial.imageUrl"
            :alt="testimonial.name"
            class="w-20 h-20 rounded-full object-cover border-4 border-blue-100 dark:border-blue-900"
          />
        </div>

        <!-- Rating -->
        <div v-if="testimonial.rating" class="flex justify-center mb-3">
          <div class="flex space-x-1">
            <svg
              v-for="star in 5"
              :key="star"
              class="w-4 h-4"
              :class="star <= testimonial.rating ? 'text-yellow-400' : 'text-gray-300 dark:text-gray-600'"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </div>
        </div>

        <!-- Text -->
        <div class="mb-4">
          <p class="text-gray-600 dark:text-gray-300 text-sm italic text-center line-clamp-3">
            "{{ testimonial.text }}"
          </p>
        </div>

        <!-- Info -->
        <div class="text-center border-t border-gray-200 dark:border-gray-700 pt-4 mb-4">
          <p class="font-semibold text-gray-900 dark:text-white text-sm">
            {{ testimonial.name }}
          </p>
          <p class="text-xs text-gray-600 dark:text-gray-400">
            {{ testimonial.role }}
          </p>
          <p class="text-xs text-blue-600 dark:text-blue-400 font-medium">
            {{ testimonial.company }}
          </p>
        </div>

        <!-- Actions -->
        <div class="flex space-x-2">
          <button
            @click="toggleVisibility(testimonial)"
            class="flex-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 px-3 py-2 rounded text-sm transition-colors"
            :title="testimonial.isVisible ? 'Verstecken' : 'Anzeigen'"
          >
            <svg
              class="w-4 h-4 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                v-if="testimonial.isVisible"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                v-if="testimonial.isVisible"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
              <path
                v-else
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
              />
            </svg>
          </button>
          <button
            @click="openEditModal(testimonial)"
            class="flex-1 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white px-3 py-2 rounded text-sm transition-colors"
          >
            Bearbeiten
          </button>
          <button
            @click="handleDelete(testimonial)"
            class="flex-1 bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 text-white px-3 py-2 rounded text-sm transition-colors"
          >
            Löschen
          </button>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div
      v-if="showModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      @click.self="closeModal"
    >
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div class="p-6">
          <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-4">
            {{ editingTestimonial ? 'Testimonial bearbeiten' : 'Neues Testimonial' }}
          </h3>

          <form @submit.prevent="handleSubmit" class="space-y-4">
            <!-- Bild Upload -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Profilbild *
              </label>
              <div v-if="form.imageUrl" class="mb-4 relative inline-block mx-auto">
                <img
                  :src="form.imageUrl"
                  alt="Preview"
                  class="w-24 h-24 rounded-full object-cover border-4 border-blue-100 dark:border-blue-900"
                />
                <button
                  v-if="editingTestimonial"
                  type="button"
                  @click="handleDeleteImage"
                  class="absolute -top-2 -right-2 bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 text-white rounded-full p-1.5 shadow-lg transition-colors"
                  title="Bild löschen"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <input
                ref="imageInput"
                type="file"
                accept="image/*"
                @change="handleImageUpload"
                class="block w-full text-sm text-gray-500 dark:text-gray-400
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100
                  dark:file:bg-blue-900 dark:file:text-blue-300
                  dark:hover:file:bg-blue-800"
              />
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Empfohlen: Quadratisches Bild (min. 200x200px)
              </p>
            </div>

            <!-- Name -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Name *
              </label>
              <input
                v-model="form.name"
                type="text"
                required
                class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                placeholder="Max Mustermann"
              />
            </div>

            <!-- Rolle -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Position/Rolle *
              </label>
              <input
                v-model="form.role"
                type="text"
                required
                class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                placeholder="CEO"
              />
            </div>

            <!-- Firma -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Firma *
              </label>
              <input
                v-model="form.company"
                type="text"
                required
                class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                placeholder="Firma GmbH"
              />
            </div>

            <!-- Projekt verknüpfen (optional) -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Mit Projekt verknüpfen (optional)
              </label>
              <select
                v-model="form.projectId"
                class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              >
                <option :value="undefined">Kein Projekt</option>
                <option
                  v-for="project in projects"
                  :key="project.id"
                  :value="project.id"
                >
                  {{ project.title }}
                </option>
              </select>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Wenn verknüpft, wird der Firmenname klickbar und leitet zum Projekt weiter
              </p>
            </div>

            <!-- Testimonial Text -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Testimonial Text *
              </label>
              <textarea
                v-model="form.text"
                required
                rows="4"
                class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                placeholder="Ein kurzer Text über die Zusammenarbeit..."
              ></textarea>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {{ form.text.length }} Zeichen (empfohlen: 100-200)
              </p>
            </div>

            <!-- Rating (Optional) -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Bewertung (optional)
              </label>
              <div class="flex items-center space-x-2">
                <button
                  v-for="star in 5"
                  :key="star"
                  type="button"
                  @click="form.rating = star"
                  class="focus:outline-none"
                >
                  <svg
                    class="w-8 h-8 transition-colors"
                    :class="form.rating && star <= form.rating ? 'text-yellow-400' : 'text-gray-300 dark:text-gray-600 hover:text-yellow-200'"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                </button>
                <button
                  v-if="form.rating"
                  type="button"
                  @click="form.rating = null"
                  class="ml-2 text-xs text-gray-500 dark:text-gray-400 hover:text-red-500 underline"
                >
                  Entfernen
                </button>
              </div>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Optional: Lasse das Rating weg, um nur Text anzuzeigen
              </p>
            </div>

            <!-- Sichtbarkeit -->
            <div class="flex items-center">
              <input
                v-model="form.isVisible"
                type="checkbox"
                id="isVisible"
                class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
              />
              <label for="isVisible" class="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                Auf der Startseite anzeigen
              </label>
            </div>

            <!-- Actions -->
            <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                @click="closeModal"
                class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                :disabled="submitting || !form.imageUrl"
                class="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {{ submitting ? 'Wird gespeichert...' : (editingTestimonial ? 'Speichern' : 'Erstellen') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import {
  getTestimonials,
  createTestimonial,
  updateTestimonial,
  deleteTestimonial,
  uploadTestimonialImage,
  deleteTestimonialImage,
  toggleTestimonialVisibility,
  getNextTestimonialOrder
} from '@/firebase/testimonials'
import { getPortfolioProjects } from '@/firebase/portfolio'
import type { Testimonial } from '@/types/testimonial'
import { useModal } from '@/composables/useModal'
import { logger } from '@/utils/logger'

const modal = useModal()

// State
const testimonials = ref<Testimonial[]>([])
const projects = ref<any[]>([])
const loading = ref(true)
const showModal = ref(false)
const submitting = ref(false)
const editingTestimonial = ref<Testimonial | null>(null)
const imageInput = ref<HTMLInputElement | null>(null)

// Projekt-Name für Testimonial anzeigen
const getProjectName = (projectId: string | undefined) => {
  if (!projectId) return null
  const project = projects.value.find(p => p.id === projectId)
  return project?.title || null
}

// Form
const form = ref({
  name: '',
  role: '',
  company: '',
  imageUrl: '',
  text: '',
  rating: null as number | null,
  projectId: undefined as string | undefined,
  order: 1,
  isVisible: true
})

// Projekte laden (nur einmal beim ersten Laden)
const loadProjects = async () => {
  if (projects.value.length === 0) {
    try {
      projects.value = await getPortfolioProjects()
    } catch (error) {
      logger.error('Fehler beim Laden der Projekte', error)
    }
  }
}

// Testimonials laden (ohne Projekte neu zu laden)
const loadTestimonials = async (showLoading = true) => {
  if (showLoading) {
    loading.value = true
  }
  try {
    testimonials.value = await getTestimonials(false) // Alle laden (auch versteckte)
  } catch (error) {
    logger.error('Fehler beim Laden der Testimonials', error)
    await modal.error('Fehler beim Laden der Testimonials', 'Ladefehler')
  } finally {
    if (showLoading) {
      loading.value = false
    }
  }
}

// Modal öffnen/schließen
const openCreateModal = async () => {
  // Stelle sicher, dass Projekte geladen sind
  await loadProjects()
  
  editingTestimonial.value = null
  form.value = {
    name: '',
    role: '',
    company: '',
    imageUrl: '',
    text: '',
    rating: null,
    projectId: undefined,
    order: await getNextTestimonialOrder(),
    isVisible: true
  }
  showModal.value = true
}

const openEditModal = async (testimonial: Testimonial) => {
  // Stelle sicher, dass Projekte geladen sind
  await loadProjects()
  
  editingTestimonial.value = testimonial
  form.value = {
    name: testimonial.name,
    role: testimonial.role,
    company: testimonial.company,
    imageUrl: testimonial.imageUrl,
    text: testimonial.text,
    rating: testimonial.rating || null,
    projectId: testimonial.projectId,
    order: testimonial.order,
    isVisible: testimonial.isVisible
  }
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
  editingTestimonial.value = null
  if (imageInput.value) {
    imageInput.value.value = ''
  }
}

// Bild hochladen
const handleImageUpload = async (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (!file) return

  // Validierung
  if (!file.type.startsWith('image/')) {
    await modal.error('Bitte wähle eine gültige Bilddatei aus.', 'Ungültiges Format')
    return
  }

  if (file.size > 5 * 1024 * 1024) { // 5MB
    await modal.error('Das Bild ist zu groß. Maximal 5MB sind erlaubt.', 'Datei zu groß')
    return
  }

  try {
    // Temporäre ID für Upload (wird später durch echte ID ersetzt)
    const tempId = editingTestimonial.value?.id || `temp-${Date.now()}`
    const url = await uploadTestimonialImage(file, tempId)
    form.value.imageUrl = url
  } catch (error) {
    logger.error('Fehler beim Hochladen des Bildes', error)
    await modal.error('Fehler beim Hochladen des Bildes', 'Upload-Fehler')
  }
}

// Bild löschen
const handleDeleteImage = async () => {
  if (!form.value.imageUrl || !editingTestimonial.value) return

  const confirmed = await modal.confirm(
    'Möchtest du das Profilbild wirklich löschen? Du musst danach ein neues Bild hochladen.',
    'Bild löschen'
  )
  if (!confirmed) return

  try {
    // Altes Bild aus Storage löschen
    await deleteTestimonialImage(form.value.imageUrl)
    
    // Bild-URL aus Form entfernen
    form.value.imageUrl = ''
    
    // File-Input zurücksetzen
    if (imageInput.value) {
      imageInput.value.value = ''
    }
    
    modal.success('Bild erfolgreich gelöscht. Bitte lade ein neues Bild hoch.')
  } catch (error) {
    logger.error('Fehler beim Löschen des Bildes', error)
    await modal.error('Fehler beim Löschen des Bildes', 'Löschfehler')
  }
}

// Submit
const handleSubmit = async () => {
  // Beim Erstellen ist ein Bild erforderlich
  if (!editingTestimonial.value && !form.value.imageUrl) {
    await modal.error('Bitte lade ein Profilbild hoch.', 'Bild fehlt')
    return
  }
  
  // Beim Bearbeiten: Wenn Bild gelöscht wurde, muss ein neues hochgeladen werden
  if (editingTestimonial.value && !form.value.imageUrl) {
    await modal.error('Bitte lade ein neues Profilbild hoch.', 'Bild fehlt')
    return
  }

  submitting.value = true
  try {
    // Konvertiere null zu undefined für rating
    const testimonialData = {
      ...form.value,
      rating: form.value.rating === null ? undefined : form.value.rating
    }
    
    if (editingTestimonial.value?.id) {
      // Update - nur imageUrl aktualisieren, wenn es geändert wurde
      await updateTestimonial(editingTestimonial.value.id, testimonialData)
    } else {
      // Create
      await createTestimonial(testimonialData)
    }
    
    // Modal sofort schließen für bessere UX
    closeModal()
    
    // Success-Message anzeigen (nicht blockierend)
    modal.success(editingTestimonial.value?.id 
      ? 'Testimonial erfolgreich aktualisiert!' 
      : 'Testimonial erfolgreich erstellt!')
    
    // Testimonials im Hintergrund neu laden (ohne Loading-State)
    loadTestimonials(false).catch(error => {
      logger.error('Fehler beim Neuladen der Testimonials', error)
    })
  } catch (error) {
    logger.error('Fehler beim Speichern des Testimonials', error)
    await modal.error('Fehler beim Speichern des Testimonials', 'Speicherfehler')
  } finally {
    submitting.value = false
  }
}

// Sichtbarkeit umschalten
const toggleVisibility = async (testimonial: Testimonial) => {
  if (!testimonial.id) return
  
  try {
    await toggleTestimonialVisibility(testimonial.id, !testimonial.isVisible)
    // Testimonials im Hintergrund neu laden (ohne Loading-State)
    loadTestimonials(false).catch(error => {
      logger.error('Fehler beim Neuladen der Testimonials', error)
    })
  } catch (error) {
    logger.error('Fehler beim Ändern der Sichtbarkeit', error)
    await modal.error('Fehler beim Ändern der Sichtbarkeit', 'Fehler')
  }
}

// Löschen
const handleDelete = async (testimonial: Testimonial) => {
  if (!testimonial.id) return

  const confirmed = await modal.confirm(
    `Möchtest du das Testimonial von "${testimonial.name}" wirklich löschen?`,
    'Testimonial löschen'
  )
  if (!confirmed) return

  try {
    await deleteTestimonial(testimonial.id, testimonial.imageUrl)
    
    // Success-Message anzeigen (nicht blockierend)
    modal.success('Testimonial erfolgreich gelöscht!')
    
    // Testimonials im Hintergrund neu laden (ohne Loading-State)
    loadTestimonials(false).catch(error => {
      logger.error('Fehler beim Neuladen der Testimonials', error)
    })
  } catch (error) {
    logger.error('Fehler beim Löschen des Testimonials', error)
    await modal.error('Fehler beim Löschen des Testimonials', 'Löschfehler')
  }
}

onMounted(async () => {
  // Lade Projekte und Testimonials parallel
  await Promise.all([
    loadProjects(),
    loadTestimonials()
  ])
})
</script>

<style scoped>
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>

